import React, {useCallback, useEffect, useState} from 'react';

import {Group, Item} from '@pb/kit-component-group';
import {PaginationStripe} from '@pb/kit-component-pagination';

import {ContractItem} from './contract-item';
import {ContractListHeader} from './contract-list-header';

import type {McpContract} from 'src/openapi/admin/models/McpContract';
import type {McpUpdateContractRequest} from 'src/openapi/admin/models/McpUpdateContractRequest';
import type {McpUpdateCreditsRequest} from 'src/openapi/admin/models/McpUpdateCreditsRequest';
import type {Pagination} from 'src/openapi/admin/models/Pagination';


type Props = {
    data: McpContract[],
    isPastContract?: boolean,
    canEdit: boolean,
    onUpdateContract: (contractId: number, data: McpUpdateContractRequest) => void,
    onUpdateCredits: (contractId: number, data: McpUpdateCreditsRequest) => void,
};

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 10;

export const ContractsList = ({
    data,
    isPastContract,
    canEdit,
    onUpdateContract,
    onUpdateCredits,
}: Props) => {
    const [expandedContractId, setExpandedContractId] = useState<number | null>(null);
    const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGE);
    const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

    const onToggle = useCallback((contractId: number) => {
        setExpandedContractId(prev => prev === contractId ? null : contractId);
    }, []);

    const onPaginationChange = useCallback((changedPagination: Pagination) => {
        setCurrentPage(changedPagination.currentPage);
        setPageSize(changedPagination.pageSize);
    }, []);

    useEffect(() => {
        setCurrentPage(DEFAULT_PAGE);
    }, [data]);

    const startIndex = pageSize * (currentPage - 1);
    const pageData = data.slice(startIndex, startIndex + pageSize);
    const canEditContract = canEdit && !isPastContract;

    return (
        <Group direction="v" gap="s">
            <Item>
                <ContractListHeader isPastContract={isPastContract}/>
                {pageData.map(contract => (
                    <ContractItem
                        key={contract.contractId}
                        contract={contract}
                        expanded={expandedContractId === contract.contractId}
                        onToggle={onToggle}
                        canEdit={canEditContract}
                        onUpdateContract={onUpdateContract}
                        onUpdateCredits={onUpdateCredits}
                    />
                ))}
            </Item>
            <PaginationStripe
                totalCount={data.length}
                currentPage={currentPage}
                pageSize={pageSize}
                onPaginationChange={onPaginationChange}
            />
        </Group>
    );
};
